import { API_CONFIG } from '@/config/api'
import { authenticatedFetch } from '../authenticatedFetch'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export enum IDPItemStatus {
  AI_GENERATED = 'AI_GENERATED',
  MANAGER_ADDED = 'MANAGER_ADDED',
  EMPLOYEE_PROPOSED = 'EMPLOYEE_PROPOSED',
  APPROVED = 'APPROVED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
}

export enum PerformanceCategory {
  EXCEPTIONAL = 'EXCEPTIONAL',
  FULL = 'FULL',
  NOT_YET_FULL = 'NOT_YET_FULL',
  WEAK = 'WEAK',
}

export enum DevelopmentFocus {
  IMPROVE_CURRENT = 'IMPROVE_CURRENT',
  ENHANCE_IMPACT = 'ENHANCE_IMPACT',
  FUTURE_ROLE = 'FUTURE_ROLE',
}

export enum DevelopmentAreaType {
  KNOWLEDGE = 'KNOWLEDGE',
  SKILLS = 'SKILLS',
  VALUES_ATTITUDE = 'VALUES_ATTITUDE',
  PERSONAL_CAPACITY = 'PERSONAL_CAPACITY',
  DIVERSITY_INCLUSION = 'DIVERSITY_INCLUSION',
}

export enum InterventionType {
  TRAINING = 'TRAINING',
  WORK_ASSIGNMENT = 'WORK_ASSIGNMENT',
  COACHING = 'COACHING',
  EDUCATION = 'EDUCATION',
}

/** The People Leader's view of the growth focus (Annual I&G Review, HR process owner, 8 Sep 2026). */
export type ReviewFocus = 'IMPROVE_CURRENT' | 'ENHANCE_IMPACT' | 'FUTURE_ROLE'

/** A next possible position the People Leader names on the plan, with the readiness for it. */
export interface NextRole {
  title: string
  readiness: 'ready-now' | 'ready-1-year' | 'ready-2-plus' | null
}

// IDP Main Entity
export interface IndividualDevelopmentPlan {
  id: string
  user_id: string
  performance_year: number
  performance_category: PerformanceCategory
  development_focus: DevelopmentFocus
  review_session_id?: string | null
  manager_approved_at?: string | null
  is_finalized: boolean
  finalized_at?: string | null
  ai_synthesis_used: boolean
  ai_synthesis_data?: Record<string, any> | null
  performance_history?: Record<string, any>[] | null
  employee_notes?: string | null
  // -------- B2 (May 2026 rework): long-running reflection fields --------
  coaching_continue?: string | null
  coaching_more_of?: string | null
  coaching_less_of?: string | null
  impact_statement?: string | null
  human_superpower?: string | null
  // -------- B3 (May 2026 rework): mobility flexibility --------
  mobility_flexibility?: 'yes' | 'maybe_future' | 'no' | null
  // -------- Annual I&G Review (HR process owner, 8 Sep 2026): the People Leader's view --------
  // Their growth focus for the person, why, and up to three next possible
  // positions with a readiness each. `review_focus_set_by` is 'talent_review'
  // when the room's action set it.
  review_focus?: ReviewFocus | string | null
  review_focus_reason?: string | null
  review_focus_set_by?: string | null
  review_focus_set_by_name?: string | null
  review_focus_set_at?: string | null
  next_roles?: NextRole[] | null
  // -------- Multi-release model --------
  // List of {review_session_id, released_at} junction rows. An IDP can
  // be released to multiple check-ins independently; each release locks
  // when its target session moves past pending/scheduled.
  releases?: Array<{
    idp_id: string
    review_session_id: string
    released_at: string
  }> | null
  // Initiation tracking
  initiated_by_role?: 'employee' | 'manager' | 'system' | null
  initiated_by_user_id?: string | null
  created_at: string
  updated_at: string

  // Related items (optional)
  strengths?: IDPStrength[]
  development_areas?: IDPDevelopmentArea[]
  interventions?: IDPIntervention[]
  career_aspirations?: CareerAspiration[]
  comments?: IDPComment[]
}

// IDP Strength
export interface IDPStrength {
  id: string
  idp_id: string
  title: string
  description?: string | null
  linked_kpa_ids?: string[] | null
  supporting_evidence_ids?: string[] | null
  employee_notes?: string | null
  manager_notes?: string | null
  item_status: IDPItemStatus
  proposed_by?: string | null
  approved_by?: string | null
  approved_at?: string | null
  rejection_reason?: string | null
  created_at: string
  updated_at: string
}

// IDP Development Area
export interface IDPDevelopmentArea {
  id: string
  idp_id: string
  title: string
  description?: string | null
  area_type: DevelopmentAreaType
  linked_kpa_ids?: string[] | null
  current_state?: string | null
  desired_state?: string | null
  employee_notes?: string | null
  manager_notes?: string | null
  priority: number // 1=high, 2=medium, 3=low
  item_status: IDPItemStatus
  proposed_by?: string | null
  approved_by?: string | null
  approved_at?: string | null
  rejection_reason?: string | null
  created_at: string
  updated_at: string
}

// IDP Intervention (Growth Enablement Objective)
export interface IDPIntervention {
  id: string
  idp_id: string
  title: string
  description?: string | null
  // Legacy scalar — always equals enablement_types[0]. Kept for older
  // surfaces that read a single type.
  intervention_type: InterventionType
  // #7: full set of enablement types for this objective (Training +
  // Coaching + Work assignment etc.). On the full-IDP GET tree the backend
  // returns [intervention_type] as a fallback; the dedicated
  // create/update intervention responses return the authoritative set.
  enablement_types?: InterventionType[] | null
  addresses_development_area_ids?: string[] | null
  time_frame?: string | null
  target_completion_date?: string | null
  success_criteria?: Array<{ criterion: string; weight: number }> | null
  resources_required?: Record<string, any> | null
  resources_allocated?: Record<string, any> | null
  progress_updates?: Array<{
    date: string
    update: string
    progress_percentage: number
  }> | null
  completion_date?: string | null
  completion_evidence?: string | null
  employee_feedback?: string | null
  item_status: IDPItemStatus
  proposed_by?: string | null
  approved_by?: string | null
  approved_at?: string | null
  rejection_reason?: string | null
  created_at: string
  updated_at: string
}

// Career Aspiration
export interface CareerAspiration {
  id: string
  idp_id: string
  aspiration_type: string
  title: string
  description?: string | null
  timeframe?: string | null
  target_date?: string | null
  function_mobility: boolean
  role_mobility: boolean
  geographic_mobility: boolean
  preferred_locations?: string[] | null
  // #3: campuses the team member would consider for a move. Populated only
  // when geographic_mobility is true. Sourced from org reference data.
  preferred_campuses?: string[] | null
  // #4: Longer-term Professional Aspirations (HR slide 8).
  future_roles_text?: string | null
  future_skills_text?: string | null
  employee_notes?: string | null
  manager_notes?: string | null
  manager_support_notes?: string | null
  progress_updates?: Array<{
    date: string
    update: string
  }> | null
  item_status: IDPItemStatus
  proposed_by?: string | null
  approved_by?: string | null
  approved_at?: string | null
  rejection_reason?: string | null
  created_at: string
  updated_at: string
}

// IDP Comment
export interface IDPComment {
  id: string
  idp_id: string
  related_item_type?: string | null
  related_item_id?: string | null
  commenter_user_id: string
  commenter_role: string
  comment_text: string
  parent_comment_id?: string | null
  created_at: string
  updated_at: string
}

// Request DTOs
export interface CreateIDPRequest {
  user_id: string
  performance_year: number
  performance_category: PerformanceCategory
  development_focus: DevelopmentFocus
  review_session_id?: string
  employee_notes?: string
  ai_synthesis_data?: Record<string, any>
  // Initiation tracking
  initiated_by_role?: 'employee' | 'manager' | 'system'
  initiated_by_user_id?: string
}

export interface UpdateIDPRequest {
  performance_category?: PerformanceCategory
  development_focus?: DevelopmentFocus
  employee_notes?: string
  // -------- B2 (May 2026 rework): long-running reflection fields --------
  // None / undefined → leave alone. Empty string → user clearing the field.
  coaching_continue?: string | null
  coaching_more_of?: string | null
  coaching_less_of?: string | null
  impact_statement?: string | null
  human_superpower?: string | null
  // -------- B3 (May 2026 rework): mobility flexibility --------
  mobility_flexibility?: 'yes' | 'maybe_future' | 'no' | null
  // -------- Annual I&G Review: the People Leader's view (theirs to change; never re-opens the plan) --------
  review_focus?: ReviewFocus | null
  review_focus_reason?: string | null
  review_focus_set_by?: string | null
  review_focus_set_by_name?: string | null
  next_roles?: NextRole[]
}

export interface CreateStrengthRequest {
  title: string
  description?: string
  linked_kpa_ids?: string[]
  supporting_evidence_ids?: string[]
  employee_notes?: string
  item_status: IDPItemStatus
}

export interface CreateDevelopmentAreaRequest {
  title: string
  description?: string
  area_type: DevelopmentAreaType
  linked_kpa_ids?: string[]
  current_state?: string
  desired_state?: string
  employee_notes?: string
  priority: number
  item_status: IDPItemStatus
}

export interface CreateInterventionRequest {
  title: string
  description?: string
  // #7: preferred — full set of enablement types. The backend derives the
  // legacy scalar intervention_type from enablement_types[0] when omitted.
  enablement_types?: InterventionType[]
  // Optional now; kept for backward compatibility. Provide either this or
  // enablement_types (422 if neither is sent).
  intervention_type?: InterventionType
  addresses_development_area_ids?: string[]
  time_frame?: string
  target_completion_date?: string
  success_criteria?: Array<{ criterion: string; weight: number }>
  resources_required?: Record<string, any>
  item_status: IDPItemStatus
}

// Career objective types (based on Gartner framework)
export type CareerObjectiveType = 'CURRENT_ROLE' | 'EXPAND_ROLE' | 'NEXT_ROLE'
// Legacy alias for backward compatibility
export type CareerGoalType = CareerObjectiveType

export interface CreateCareerAspirationRequest {
  aspiration_type: CareerObjectiveType | string
  title: string
  description?: string
  timeframe?: string
  target_date?: string
  function_mobility: boolean
  role_mobility: boolean
  geographic_mobility: boolean
  preferred_locations?: string[]
  // #3: campuses considered for a move (set only when geographic_mobility).
  preferred_campuses?: string[]
  // #4: Longer-term Professional Aspirations (HR slide 8).
  future_roles_text?: string
  future_skills_text?: string
  employee_notes?: string
  item_status: IDPItemStatus
  // Target position for NEXT_ROLE
  target_position_id?: string
  target_position_title?: string
  // Long-term career objective — legacy, retained in API but dropped from
  // the UI per the #4 brief.
  long_term_objective_id?: string
  long_term_objective_title?: string
}

export interface ApprovalRequest {
  approved: boolean
  rejection_reason?: string
  manager_notes?: string
  resources_allocated?: Record<string, any>
}

export interface IDPListResponse {
  idps: IndividualDevelopmentPlan[]
  total: number
  page: number
  page_size: number
}

// ============================================================================
// API SERVICE
// ============================================================================

const BASE_URL = `${API_CONFIG.baseURL}/api/v1/idp`

class IDPService {

  // ========================================================================
  // CENTRALIZED REQUEST
  // ========================================================================

  private async request<T>(url: string, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers as Record<string, string>),
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }

    if (response.status === 204) {
      return undefined as T
    }

    return response.json()
  }

  // ========================================================================
  // IDP CRUD
  // ========================================================================

  async getUserIDPs(userId: string, year?: number): Promise<IDPListResponse> {
    const params = year ? `?year=${year}` : ''
    return this.request<IDPListResponse>(`${BASE_URL}/users/${userId}/idps${params}`)
  }

  /** Count team members whose IDP has an item awaiting the People Leader's review
   *  (>=1 EMPLOYEE_PROPOSED item). One bulk call — no per-member fan-out. */
  async getTeamNeedsReviewCount(userIds: string[], year?: number): Promise<{ count: number }> {
    return this.request<{ count: number }>(`${BASE_URL}/team/needs-review-count`, {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds, year }),
    })
  }

  async getIDP(idpId: string): Promise<IndividualDevelopmentPlan> {
    return this.request<IndividualDevelopmentPlan>(`${BASE_URL}/idps/${idpId}`)
  }

  async createIDP(userId: string, data: CreateIDPRequest): Promise<IndividualDevelopmentPlan> {
    return this.request<IndividualDevelopmentPlan>(`${BASE_URL}/users/${userId}/idps`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateIDP(idpId: string, data: UpdateIDPRequest): Promise<IndividualDevelopmentPlan> {
    return this.request<IndividualDevelopmentPlan>(`${BASE_URL}/idps/${idpId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * B3 (May 2026 rework): release the whole IDP to a specific check-in
   * (Mid-Year / Year-End). Only the IDP owner can release. Target
   * session must be pending / scheduled — 409 once it's in-progress.
   */
  async releaseIDP(idpId: string, reviewSessionId: string): Promise<IndividualDevelopmentPlan> {
    const qs = new URLSearchParams({ review_session_id: reviewSessionId })
    return this.request<IndividualDevelopmentPlan>(
      `${BASE_URL}/idps/${idpId}/release?${qs.toString()}`,
      { method: 'POST' },
    )
  }

  /**
   * Withdraw ONE release of the IDP from a specific check-in. Multi-
   * release model — leaves any other releases intact. Refused once the
   * target session has progressed past pending / scheduled. Idempotent
   * when no release exists for the target session.
   */
  async withdrawIDP(idpId: string, reviewSessionId: string): Promise<IndividualDevelopmentPlan> {
    const qs = new URLSearchParams({ review_session_id: reviewSessionId })
    return this.request<IndividualDevelopmentPlan>(
      `${BASE_URL}/idps/${idpId}/withdraw?${qs.toString()}`,
      { method: 'POST' },
    )
  }

  // ========================================================================
  // STRENGTH OPERATIONS
  // ========================================================================

  async addStrength(idpId: string, data: CreateStrengthRequest): Promise<IDPStrength> {
    return this.request<IDPStrength>(`${BASE_URL}/idps/${idpId}/strengths`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateStrength(idpId: string, strengthId: string, data: CreateStrengthRequest): Promise<IDPStrength> {
    return this.request<IDPStrength>(`${BASE_URL}/idps/${idpId}/strengths/${strengthId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteStrength(idpId: string, strengthId: string): Promise<void> {
    await this.request<void>(`${BASE_URL}/idps/${idpId}/strengths/${strengthId}`, {
      method: 'DELETE',
    })
  }

  // ========================================================================
  // DEVELOPMENT AREA OPERATIONS
  // ========================================================================

  async addDevelopmentArea(idpId: string, data: CreateDevelopmentAreaRequest): Promise<IDPDevelopmentArea> {
    return this.request<IDPDevelopmentArea>(`${BASE_URL}/idps/${idpId}/development-areas`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateDevelopmentArea(idpId: string, areaId: string, data: CreateDevelopmentAreaRequest): Promise<IDPDevelopmentArea> {
    return this.request<IDPDevelopmentArea>(`${BASE_URL}/idps/${idpId}/development-areas/${areaId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteDevelopmentArea(idpId: string, areaId: string): Promise<void> {
    await this.request<void>(`${BASE_URL}/idps/${idpId}/development-areas/${areaId}`, {
      method: 'DELETE',
    })
  }

  // ========================================================================
  // INTERVENTION OPERATIONS
  // ========================================================================

  async addIntervention(idpId: string, data: CreateInterventionRequest): Promise<IDPIntervention> {
    return this.request<IDPIntervention>(`${BASE_URL}/idps/${idpId}/interventions`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateIntervention(idpId: string, interventionId: string, data: CreateInterventionRequest): Promise<IDPIntervention> {
    return this.request<IDPIntervention>(`${BASE_URL}/idps/${idpId}/interventions/${interventionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteIntervention(idpId: string, interventionId: string): Promise<void> {
    await this.request<void>(`${BASE_URL}/idps/${idpId}/interventions/${interventionId}`, {
      method: 'DELETE',
    })
  }

  // ========================================================================
  // CAREER ASPIRATION OPERATIONS
  // ========================================================================

  async addCareerAspiration(idpId: string, data: CreateCareerAspirationRequest): Promise<CareerAspiration> {
    return this.request<CareerAspiration>(`${BASE_URL}/idps/${idpId}/career-aspirations`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateCareerAspiration(idpId: string, aspirationId: string, data: CreateCareerAspirationRequest): Promise<CareerAspiration> {
    return this.request<CareerAspiration>(`${BASE_URL}/idps/${idpId}/career-aspirations/${aspirationId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteCareerAspiration(idpId: string, aspirationId: string): Promise<void> {
    await this.request<void>(`${BASE_URL}/idps/${idpId}/career-aspirations/${aspirationId}`, {
      method: 'DELETE',
    })
  }

  // ========================================================================
  // MANAGER ACTIONS (For manager UI integration)
  // ========================================================================

  async approveOrRejectItem(
    itemType: 'strength' | 'development_area' | 'intervention' | 'career_aspiration',
    itemId: string,
    approval: ApprovalRequest
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      `${BASE_URL}/idps/items/${itemType}/${itemId}/approve`,
      {
        method: 'POST',
        body: JSON.stringify(approval),
      }
    )
  }

  async approveIDPItem(
    idpId: string,
    itemType: 'strength' | 'development_area' | 'intervention' | 'career_aspiration',
    itemId: string,
    notes?: string
  ): Promise<void> {
    const endpoint = {
      strength: `strengths/${itemId}/approve`,
      development_area: `development-areas/${itemId}/approve`,
      intervention: `interventions/${itemId}/approve`,
      career_aspiration: `career-aspirations/${itemId}/approve`
    }[itemType]

    await this.request<void>(`${BASE_URL}/idps/${idpId}/${endpoint}`, {
      method: 'POST',
      body: JSON.stringify({ manager_notes: notes }),
    })
  }

  async rejectIDPItem(
    idpId: string,
    itemType: 'strength' | 'development_area' | 'intervention' | 'career_aspiration',
    itemId: string,
    reason: string
  ): Promise<void> {
    const endpoint = {
      strength: `strengths/${itemId}/reject`,
      development_area: `development-areas/${itemId}/reject`,
      intervention: `interventions/${itemId}/reject`,
      career_aspiration: `career-aspirations/${itemId}/reject`
    }[itemType]

    await this.request<void>(`${BASE_URL}/idps/${idpId}/${endpoint}`, {
      method: 'POST',
      body: JSON.stringify({ rejection_reason: reason }),
    })
  }

  async addManagerNotes(
    idpId: string,
    itemType: 'strength' | 'development_area' | 'intervention' | 'career_aspiration',
    itemId: string,
    notes: string
  ): Promise<void> {
    const endpoint = {
      strength: `strengths/${itemId}/notes`,
      development_area: `development-areas/${itemId}/notes`,
      intervention: `interventions/${itemId}/notes`,
      career_aspiration: `career-aspirations/${itemId}/notes`
    }[itemType]

    await this.request<void>(`${BASE_URL}/idps/${idpId}/${endpoint}`, {
      method: 'POST',
      body: JSON.stringify({ manager_notes: notes }),
    })
  }

  async finalizeIDP(idpId: string): Promise<IndividualDevelopmentPlan> {
    return this.request<IndividualDevelopmentPlan>(`${BASE_URL}/idps/${idpId}/finalize`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }
}

export const idpService = new IDPService()
